/**
 * Search analytics via navigator.sendBeacon → the proxy's /events → D1.
 *
 * Two events:
 *  - `search`: one per SETTLED query, not per keystroke. A query settles after
 *    a pause in typing, or straight away when the user clicks a result,
 *    submits, or leaves the page. Typing "v", "ve", "verbier" logs "verbier".
 *  - `result_click`: fired the moment a result is clicked, before navigation.
 *
 * sendBeacon queues the POST in the browser so it survives the page unload.
 * The payload is text/plain (a CORS-safelisted type), so the cross-origin
 * beacon needs no preflight. No IP, cookie or user id is sent: `sid` is a
 * random id per page view, held only in memory, which lets the dashboard join
 * a search to its clicks (click-through rate).
 *
 * Every search box on a page shares ONE tracker (pageTracker), so a visit has
 * one sid. Each event says which box it came from (`source`) and the page path.
 * The theme's own search forms (Find a rep, shop, listing filters) report
 * through the same tracker by dispatching a DOM event — no config needed:
 *
 *   document.dispatchEvent(new CustomEvent("scgb-search:track", { detail: {
 *     type: "search", source: "reps", q: "sarah", total: 3 } }));
 *   // on a result pick:
 *   { type: "result_click", source: "reps", q: "sarah", clickedUrl: "/reps/sarah/", pos: 1, total: 3 }
 *   // add flush: true to a search to log it straight away.
 *
 * HANDOFF to a results page. When a box sends the visitor to a results page
 * (Enter / submit), the box does NOT log the search; the results page logs it
 * once, credited to the box. The box name rides in the URL as `src`
 * (`/?s=verbier&src=header`), so no cookie or storage is involved. A theme
 * form does the same with a hidden `<input name="src" value="reps">` and, on
 * submit, `{ type: "submit", source: "reps" }` to drop its pending search.
 *
 * Searches still settle (2.5s pause) before they are logged, so a form can
 * report every keystroke's result count.
 */

export interface ResultClickEvent {
  type: "result_click";
  q: string; // query as typed at click time
  clickedId: string; // e.g. sc_resort-1591
  clickedUrl: string;
  pos: number; // 1-based rank of the clicked result
  tab: string;
  mode: string; // "modal" | "results" | "form"
  total: number; // result-set size
  source?: string; // which search box
}

export interface SearchEvent {
  type: "search";
  q: string;
  tab: string;
  mode: string;
  scope: string;
  total: number;
  /** Which search box (header, hero, results, reps, ...). */
  source?: string;
  /** Holiday departures panel count, when it was shown. */
  departuresTotal?: number;
  /** The holiday filters the plain-English parse applied. */
  nlFilters?: unknown;
}

type AnyEvent = (ResultClickEvent | SearchEvent) & { sid: string };

const endpoint = import.meta.env.VITE_SEARCH_EVENTS_URL as string | undefined;

function randomSid(): string {
  const c = globalThis.crypto;
  if (c?.randomUUID) return c.randomUUID();
  return Math.random().toString(16).slice(2).padEnd(16, "0");
}

function pagePath(): string {
  return typeof location !== "undefined" ? location.pathname : "";
}

export function sendEvent(ev: AnyEvent): void {
  const body = JSON.stringify({ page: pagePath(), ...ev, ts: Date.now() });
  if (endpoint && typeof navigator !== "undefined" && navigator.sendBeacon) {
    try {
      navigator.sendBeacon(endpoint, new Blob([body], { type: "text/plain" }));
      return;
    } catch {
      /* fall through to dev logging */
    }
  }
  // No endpoint configured (e.g. local dev / standalone harness): surface the
  // event so it can be verified in the console during a smoke test.
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug("[scgb-search] event", body);
  }
}

/** How long typing must pause before a search counts. */
export const SETTLE_MS = 2500;
/** How long a settled search may wait for the holiday panel's numbers. */
export const DEPARTURES_WAIT_MS = 6000;

/**
 * One per page view (see pageTracker). Holds the latest search until it
 * settles, then beacons it once. A repeat of the last logged query+tab is
 * skipped, so re-running the same search doesn't double count.
 */
export class SearchTracker {
  readonly sid = randomSid();
  private pending: SearchEvent | null = null;
  private awaitingDepartures = false;
  private pendingSince = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private lastKey = "";

  constructor(
    private send: (ev: AnyEvent) => void = sendEvent,
    private now: () => number = Date.now,
  ) {}

  /** A search returned results. `awaitingDepartures`: the holiday panel is
   *  still loading, so hold the event for its numbers (up to a limit). */
  searched(ev: Omit<SearchEvent, "type">, awaitingDepartures = false): void {
    // Moving to a different box settles what was typed in the last one.
    if (this.pending && (this.pending.source ?? "") !== (ev.source ?? "")) this.flush();
    this.pending = { type: "search", ...ev };
    this.awaitingDepartures = awaitingDepartures;
    this.pendingSince = this.now();
    this.schedule(SETTLE_MS);
  }

  /** The holiday panel answered for `q`. Ignored if the user has moved on. */
  departures(q: string, departuresTotal: number, nlFilters: unknown): void {
    if (!this.pending || this.pending.q !== q) return;
    this.pending = { ...this.pending, departuresTotal, nlFilters };
    this.awaitingDepartures = false;
  }

  /** Drop the pending search without logging it: the visitor is going to a
   *  results page, which logs it (see HANDOFF above). Only drops a search from
   *  `source` when given. */
  discard(source?: string): void {
    if (!this.pending) return;
    if (source !== undefined && (this.pending.source ?? "") !== source) return;
    this.clearTimer();
    this.pending = null;
  }

  /** Log the pending search now (result click, page hide). */
  flush(): void {
    this.clearTimer();
    const ev = this.pending;
    this.pending = null;
    if (!ev || !ev.q) return;
    const key = `${ev.q.toLowerCase().replace(/\s+/g, " ").trim()}|${ev.tab}|${ev.mode}|${ev.source ?? ""}`;
    if (key === this.lastKey) return;
    this.lastKey = key;
    this.send({ ...ev, sid: this.sid });
  }

  click(ev: ResultClickEvent): void {
    this.flush(); // the search that led to the click is logged first
    this.send({ ...ev, sid: this.sid });
  }

  private schedule(ms: number): void {
    this.clearTimer();
    this.timer = setTimeout(() => {
      this.timer = null;
      const waited = this.now() - this.pendingSince;
      if (this.awaitingDepartures && waited < DEPARTURES_WAIT_MS) {
        this.schedule(Math.min(500, DEPARTURES_WAIT_MS - waited));
        return;
      }
      this.flush();
    }, ms);
  }

  private clearTimer(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }
}

/** The proxy's "Popular searches" list for this page's site, or [] on any failure. */
export async function fetchPopular(host: string, fetchImpl: typeof fetch = fetch): Promise<string[]> {
  try {
    const res = await fetchImpl(`${host.replace(/\/$/, "")}/popular`, { method: "GET" });
    if (!res.ok) return [];
    const body = (await res.json()) as unknown;
    if (!Array.isArray(body)) return [];
    return body
      .map((p: unknown) => (typeof p === "string" ? p : ((p as { query?: unknown })?.query ?? "")))
      .filter((s): s is string => typeof s === "string" && s.length > 0)
      .slice(0, 6);
  } catch {
    return [];
  }
}

const TRACK_EVENT = "scgb-search:track";
let shared: SearchTracker | null = null;

function str(v: unknown, max: number): string {
  return typeof v === "string" ? v.slice(0, max) : "";
}

function num(v: unknown): number {
  return typeof v === "number" && Number.isFinite(v) ? Math.max(0, Math.trunc(v)) : 0;
}

/** Handle a theme form's report (detail of a `scgb-search:track` DOM event). */
export function trackFromDetail(t: SearchTracker, d: unknown): void {
  if (!d || typeof d !== "object") return;
  const e = d as Record<string, unknown>;
  const source = str(e.source, 32);
  const q = str(e.q, 200).trim();
  if (!source || (!q && e.type !== "submit")) return;
  if (e.type === "submit") {
    t.discard(source);
    return;
  }
  if (e.type === "search") {
    t.searched({ q, tab: str(e.tab, 32), mode: "form", scope: "", total: num(e.total), source });
    if (e.flush === true) t.flush();
  } else if (e.type === "result_click") {
    t.click({
      type: "result_click",
      q,
      clickedId: str(e.clickedId, 64),
      clickedUrl: str(e.clickedUrl, 300),
      pos: num(e.pos),
      tab: str(e.tab, 32),
      mode: "form",
      total: num(e.total),
      source,
    });
  }
}

/** The page's one tracker. First call wires page-hide flushing and the
 *  `scgb-search:track` listener for the theme's own search forms. */
export function pageTracker(): SearchTracker {
  if (shared) return shared;
  const t = new SearchTracker();
  shared = t;
  if (typeof window !== "undefined" && typeof document !== "undefined") {
    window.addEventListener("pagehide", () => t.flush());
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") t.flush();
    });
    document.addEventListener(TRACK_EVENT, (ev) => trackFromDetail(t, (ev as CustomEvent).detail));
  }
  return t;
}

const SLUG = /^[a-z0-9-]{1,32}$/;

/** The box that sent the visitor to this results page (`src` in the URL), or
 *  "". Removes `src` from the address bar, so a shared or reloaded link isn't
 *  credited again. */
export function takeHandoffSource(): string {
  if (typeof window === "undefined") return "";
  const p = new URLSearchParams(window.location.search);
  const src = p.get("src") ?? "";
  if (!p.has("src")) return "";
  p.delete("src");
  const next = `${window.location.pathname}?${p.toString()}`.replace(/\?$/, "") + window.location.hash;
  window.history.replaceState(window.history.state, "", next);
  return SLUG.test(src) ? src : "";
}

/** A results-page link that credits `source`. */
export function resultsUrl(q: string, source: string): string {
  const src = SLUG.test(source) ? `&src=${source}` : "";
  return `/?s=${encodeURIComponent(q)}${src}`;
}
